import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { COR_PRIMARIA_PDF } from '@/nucleo/pdf/cores';
import type { GovernancaIndicadores } from '../governanca-Institucional/useGovernancaInstitucional';

const ROTULOS_OPERACAO: Record<string, string> = {
  login_portal_credenciado: 'Login Portal Credenciado',
  login_portal_admin: 'Login Portal Admin',
  assinatura_termo: 'Assinatura de Termo',
  assinatura_aditivo: 'Assinatura de Aditivo',
  assinatura_distrato: 'Assinatura de Distrato',
  inscricao_publica: 'Inscrição Pública',
  consulta_publica: 'Consulta Pública',
};

const ROTULOS_TIPO_DOC: Record<string, string> = {
  termo: 'Termo de Adesão',
  aditivo: 'Aditivo',
  distrato: 'Distrato',
  contrato: 'Contrato',
  declaracao: 'Declaração',
  parecer: 'Parecer',
};

export function exportGovernancaInstitucionalPDF(data: GovernancaIndicadores): void {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const margin = 15;
  let y = margin;

  // Cabeçalho
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Política Institucional Consolidada', margin, y);
  y += 6;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Gov.BR + Assinatura Digital — Sistema TEG / Prefeitura de São Paulo', margin, y);
  y += 5;
  doc.text(`Gerado em: ${new Date(data.gerado_em).toLocaleString('pt-BR')}`, margin, y);
  y += 8;

  // KPIs
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Indicadores-chave', margin, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const kpiLines = [
    `Cobertura Gov.BR (inscrições): ${data.kpis.cobertura_govbr_pct}% (${data.kpis.inscricoes_govbr}/${data.kpis.total_inscricoes})`,
    `Cobertura assinaturas (termos ATIVO/VIGENTE): ${data.kpis.cobertura_assinatura_pct}% (${data.kpis.termos_com_assinatura}/${data.kpis.total_termos_ativos})`,
    `Total de assinaturas registradas: ${data.kpis.total_assinaturas}`,
    `Integridade da hash chain: ${data.kpis.integridade_pct}%`,
  ];
  kpiLines.forEach((l) => {
    doc.text(`• ${l}`, margin, y);
    y += 5;
  });
  y += 4;

  // Tabela: Cobertura Gov.BR
  autoTable(doc, {
    startY: y,
    head: [['Operação', 'Selo mínimo', 'Status', 'Descrição']],
    body: data.govbr_cobertura.map((c) => [
      ROTULOS_OPERACAO[c.operacao] ?? c.operacao,
      c.selo_minimo.toUpperCase(),
      c.ativo ? 'Ativo' : 'Inativo',
      c.descricao ?? '-',
    ]),
    styles: { fontSize: 9 },
    headStyles: { fillColor: COR_PRIMARIA_PDF },
    margin: { left: margin, right: margin },
  });
  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

  if (y > 240) {
    doc.addPage();
    y = margin;
  }

  // Política de assinatura por tipo
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Política de assinatura por tipo de documento', margin, y);
  y += 4;
  autoTable(doc, {
    startY: y,
    head: [
      [
        'Tipo de documento',
        'Nível mínimo',
        'Selo Gov.BR',
        'Provedores aceitos',
        'Base legal',
        'Status',
      ],
    ],
    body: data.politica.map((p) => [
      ROTULOS_TIPO_DOC[p.tipo_documento] ?? p.tipo_documento,
      p.nivel_minimo,
      p.selo_govbr_minimo?.toUpperCase() ?? '—',
      (p.provedores ?? [])
        .filter((pr) => pr.ativo)
        .map((pr) => pr.provedor)
        .join(', ') || '—',
      p.base_legal,
      p.ativo ? 'Ativo' : 'Inativo',
    ]),
    styles: { fontSize: 8, cellWidth: 'wrap' },
    headStyles: { fillColor: COR_PRIMARIA_PDF },
    margin: { left: margin, right: margin },
  });
  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

  if (y > 240) {
    doc.addPage();
    y = margin;
  }

  // Cobertura por DRE
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Cobertura de assinaturas por DRE (termos ATIVO/VIGENTE)', margin, y);
  y += 4;
  autoTable(doc, {
    startY: y,
    head: [['DRE', 'Total termos', 'Com assinatura', 'Cobertura']],
    body: data.termos_por_dre.map((d) => [
      d.dre,
      String(d.total),
      String(d.com_assinatura),
      `${d.percentual}%`,
    ]),
    styles: { fontSize: 9 },
    headStyles: { fillColor: COR_PRIMARIA_PDF },
    margin: { left: margin, right: margin },
  });
  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;

  if (y > 250) {
    doc.addPage();
    y = margin;
  }

  // Integridade
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Integridade da trilha imutável (SHA-256)', margin, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const intLines = [
    `Termos com assinaturas: ${
      data.integridade.termos_integros + data.integridade.termos_quebrados
    }`,
    `Termos íntegros: ${data.integridade.termos_integros}`,
    `Termos com cadeia quebrada: ${data.integridade.termos_quebrados}`,
    `Cadeias quebradas (registros): ${data.integridade.cadeias_quebradas}`,
    `Percentual íntegro: ${data.integridade.percentual_integro}%`,
  ];
  intLines.forEach((l) => {
    doc.text(`• ${l}`, margin, y);
    y += 5;
  });

  // Rodapé
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(110);
    doc.text(`Documento de auditoria SME/CGM — Página ${i} de ${pageCount}`, margin, 290);
    doc.setTextColor(0);
  }

  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`politica-institucional-consolidada-${stamp}.pdf`);
}
